import React from 'react';
import { Form, Input, Modal, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { ClientBriefDto, ClientType } from 'api/models';
import { Button } from 'components/buttons/buttons';
import { BasicFormWrapper } from 'container/styled';
import FeatherIcon from 'feather-icons-react';
import openNotificationWithIcon from 'utility/notification';
import { ClientsApi } from 'api/api';

interface ClientFormProps {
  visible: boolean;
  editMode: boolean;
  client?: ClientBriefDto | null;
  onCancel: () => void;
  onSuccess: (clientId: number) => Promise<void>;
}

const clientsApi = new ClientsApi();

export const ClientForm: React.FC<ClientFormProps> = ({ visible, editMode, client, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [clientType, setClientType] = React.useState<ClientType>(ClientType.NUMBER_0);

  React.useEffect(() => {
    if (client && editMode) {
      form.setFieldsValue({
        id: client.id,
        name: client.name,
        type: client.type,
        companyName: client.companyName,
        email: client.email,
        phoneNumber: client.phoneNumber,
        personalId: client.personalId,
        companyRegistrationId: client.companyRegistrationId,
        taxNumber: client.taxNumber,
        address: client.address,
      });
      setClientType(client.type as ClientType);
    }
  }, [client, editMode, form]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      let response: any;
      if (editMode) {
        response = await clientsApi.apiClientsIdPut({
          id: values.id,
          updateClientCommand: values,
        });
      } else {
        response = await clientsApi.apiClientsPost({
          createClientCommand: values,
        });
      }

      openNotificationWithIcon(
        'success',
        t(`clients:${editMode ? 'updated' : 'created'}`, `Client ${editMode ? 'updated' : 'created'} successfully`),
      );
      onSuccess(response.data);
    } catch (err) {
      openNotificationWithIcon('error', t('clients:error', 'Error occurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        title={t(`clients:${editMode ? 'edit' : 'create'}`, `${editMode ? 'Edit' : 'Create'} Client`)}
        visible={visible}
        onCancel={onCancel}
        footer={null}
        width={700}
      >
        <BasicFormWrapper>
          <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ type: ClientType.NUMBER_0 }}>
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>

            <Form.Item label={t('clients:type', 'Client Type')} name="type" rules={[{ required: true }]}>
              <Select onChange={value => setClientType(value)}>
                <Select.Option value={ClientType.NUMBER_0}>{t('clients:individual', 'Individual')}</Select.Option>
                <Select.Option value={ClientType.NUMBER_1}>{t('clients:company', 'Company')}</Select.Option>
              </Select>
            </Form.Item>

            {clientType === ClientType.NUMBER_0 && (
              <>
                <Form.Item
                  label={t('clients:name', 'Name')}
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: t('clients:name-required', 'Name is required'),
                    },
                    {
                      max: 200,
                      message: t('clients:name-max', 'Name cannot exceed 200 characters'),
                    },
                  ]}
                >
                  <Input prefix={<FeatherIcon icon="user" size={16} />} />
                </Form.Item>

                <Form.Item
                  label={t('clients:personal-id', 'Personal ID')}
                  name="personalId"
                  rules={[
                    {
                      max: 50,
                      message: t('clients:personal-id-max', 'Personal ID cannot exceed 50 characters'),
                    },
                  ]}
                >
                  <Input prefix={<FeatherIcon icon="hash" size={16} />} />
                </Form.Item>
              </>
            )}

            {clientType === ClientType.NUMBER_1 && (
              <>
                <Form.Item
                  label={t('clients:company-name', 'Company Name')}
                  name="companyName"
                  rules={[
                    {
                      required: true,
                      message: t('clients:company-name-required', 'Company name is required'),
                    },
                    {
                      max: 200,
                      message: t('clients:company-name-max', 'Company name cannot exceed 200 characters'),
                    },
                  ]}
                >
                  <Input prefix={<FeatherIcon icon="briefcase" size={16} />} />
                </Form.Item>

                <Form.Item
                  label={t('clients:company-id', 'Company Registration ID')}
                  name="companyRegistrationId"
                  rules={[
                    {
                      max: 50,
                      message: t('clients:company-id-max', 'Company ID cannot exceed 50 characters'),
                    },
                  ]}
                >
                  <Input prefix={<FeatherIcon icon="hash" size={16} />} />
                </Form.Item>

                <Form.Item
                  label={t('clients:tax-number', 'Tax Number')}
                  name="taxNumber"
                  rules={[
                    {
                      max: 50,
                      message: t('clients:tax-number-max', 'Tax number cannot exceed 50 characters'),
                    },
                  ]}
                >
                  <Input prefix={<FeatherIcon icon="hash" size={16} />} />
                </Form.Item>
              </>
            )}

            <Form.Item
              label={t('clients:email', 'Email')}
              name="email"
              rules={[
                {
                  type: 'email',
                  message: t('clients:email-invalid', 'Invalid email format'),
                },
              ]}
            >
              <Input prefix={<FeatherIcon icon="mail" size={16} />} />
            </Form.Item>

            <Form.Item
              label={t('clients:phone', 'Phone Number')}
              name="phoneNumber"
              rules={[
                {
                  pattern: /^\+?[\d\s-]+$/,
                  message: t('clients:phone-invalid', 'Invalid phone number format'),
                },
              ]}
            >
              <Input prefix={<FeatherIcon icon="phone" size={16} />} />
            </Form.Item>

            <Form.Item
              label={t('clients:address', 'Address')}
              name="address"
              rules={[
                {
                  max: 500,
                  message: t('clients:address-max', 'Address cannot exceed 500 characters'),
                },
              ]}
            >
              <Input prefix={<FeatherIcon icon="map-pin" size={16} />} />
            </Form.Item>

            <div className="form-actions">
              <Button onClick={onCancel} style={{ marginRight: 8 }}>
                {t('global:cancel', 'Cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editMode ? t('global:update', 'Update') : t('global:create', 'Create')}
              </Button>
            </div>
          </Form>
        </BasicFormWrapper>
      </Modal>
    </>
  );
};
